import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import api from "../services/api";
import { useUser } from "../context/UserContext";
import { getDefaultAuthenticatedRoute, getStrongAuthStatus } from "../utils/strongAuth";

export default function MfaSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useUser();
  const [status, setStatus] = useState({ enabled: false, pendingSetup: false });
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/mfa/status");
      setStatus(data || {});
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "MFA-status ophalen mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  function completeEnrollment(data, fallbackMessage) {
    const accessToken = String(data?.accessToken || "").trim();
    if (!accessToken) {
      throw new Error("De beveiligingsstatus kon niet worden vernieuwd.");
    }
    login(accessToken, String(data?.refreshToken || "").trim());
    setBackupCodes(data?.recoveryCodes || data?.backupCodes || []);
    setStatus((previous) => ({
      ...previous,
      enabled: Boolean(previous?.enabled || data?.enabled),
      strongAuth: {
        ...(previous?.strongAuth || {}),
        ...getStrongAuthStatus(data),
        strongAuthEnrolled: true,
        enrollmentRequired: false,
      },
    }));
    return data?.message || fallbackMessage;
  }

  async function registerPasskey() {
    if (!window.PublicKeyCredential) {
      setError("Passkeys worden niet ondersteund op dit apparaat of in deze browser.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const start = await api.post("/auth/passkeys/register/start", { name: "Mijn apparaat" });
      const response = await startRegistration({ optionsJSON: start.data.options });
      const finish = await api.post("/auth/passkeys/register/finish", {
        challengeToken: start.data.challengeToken,
        response,
        name: "Mijn apparaat",
      });
      completeEnrollment(finish.data, "Passkey geregistreerd.");
      await loadStatus();
    } catch (requestError) {
      const cancelled = requestError?.name === "AbortError" || String(requestError?.message || "").toLowerCase().includes("cancel");
      if (!cancelled) {
        setError(requestError?.response?.data?.message || requestError?.message || "Passkey registreren mislukt.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function startSetup() {
    if (!currentPassword) {
      setError("Vul je huidige wachtwoord in.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const { data } = await api.post("/auth/mfa/setup/start", { currentPassword });
      setSetup(data);
      setCode("");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "MFA-setup starten mislukt.");
    } finally {
      setSubmitting(false);
    }
  }

  async function verifySetup() {
    if (!code.trim()) {
      setError("Vul de code uit je authenticator-app in.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const { data } = await api.post("/auth/mfa/setup/verify", { code: code.trim() });
      completeEnrollment(data, "MFA ingeschakeld.");
      await loadStatus();
      setSetup(null);
      setCurrentPassword("");
      setCode("");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "MFA verifiëren mislukt.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-slate-100">MFA-status laden...</div>;
  }

  const strongAuth = getStrongAuthStatus(status);
  const enrolled = status.enabled || strongAuth.strongAuthEnrolled;
  const queryReturnTo = new URLSearchParams(location.search).get("returnTo") || "";
  const stateReturnTo = location.state?.from?.pathname || "";
  const returnTo = [stateReturnTo, queryReturnTo].find(
    (path) => path.startsWith("/") && path !== "/mfa/setup" && path !== "/login"
  ) || getDefaultAuthenticatedRoute(user);

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-10">
      <section className="mx-auto max-w-xl rounded-xl bg-white p-6 shadow">
        <h1 className="text-2xl font-bold text-slate-900">Veilig inloggen instellen</h1>
        <p className="mt-2 text-slate-700">Gebruik bij voorkeur een passkey: vingerafdruk, Face ID, Windows Hello of je apparaatpincode. Biometrische gegevens blijven op je apparaat.</p>

        {error && <p className="mt-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}

        {enrolled ? (
          <div className="mt-5 space-y-4">
            <p className="rounded bg-green-50 p-3 text-green-800">Sterke authenticatie is ingesteld.</p>
            {backupCodes.length > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 p-4">
                <p className="font-semibold text-amber-900">Bewaar deze backupcodes veilig. Ze worden maar één keer getoond.</p>
                <ul className="mt-3 space-y-1 font-mono text-sm text-amber-950">
                  {backupCodes.map((backupCode) => <li key={backupCode}>{backupCode}</li>)}
                </ul>
              </div>
            )}
            <button type="button" onClick={() => navigate(returnTo, { replace: true })} className="rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700">Verder naar Logboek24</button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="rounded border border-blue-200 bg-blue-50 p-4">
              <h2 className="font-semibold text-slate-900">Aanbevolen: passkey</h2>
              <p className="mt-1 text-sm text-slate-700">Werkt met vingerafdruk, Face ID, Windows Hello, apparaatpincode en geschikte security keys.</p>
              <button type="button" onClick={registerPasskey} disabled={submitting} className="mt-3 rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">{submitting ? "Bezig..." : "Passkey toevoegen"}</button>
            </div>
            <p className="text-sm font-medium text-slate-700">Of gebruik een authenticator-app</p>
            {!setup ? (
              <>
                <label className="block text-sm font-medium text-slate-800" htmlFor="mfa-current-password">Huidig wachtwoord</label>
                <input id="mfa-current-password" type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2" />
                <button type="button" onClick={startSetup} disabled={submitting} className="rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">{submitting ? "Setup starten..." : "Start MFA-setup"}</button>
              </>
            ) : (
              <>
                <p className="text-slate-700">Scan de QR-code met Microsoft Authenticator en voer daarna de zescijferige code in.</p>
                {setup.qrCodeDataUrl && <img src={setup.qrCodeDataUrl} alt="MFA QR-code" className="h-48 w-48 border bg-white p-1" />}
                <p className="break-all text-sm text-slate-700">Handmatige sleutel: <span className="font-mono">{setup.secret || setup.secretMasked}</span></p>
                <label className="block text-sm font-medium text-slate-800" htmlFor="mfa-code">Authenticator-code</label>
                <input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2" />
                <button type="button" onClick={verifySetup} disabled={submitting} className="rounded bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-60">{submitting ? "Verifiëren..." : "Bevestig en activeer MFA"}</button>
              </>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
